/**
 * Adapted from @author Antoine Vandecreme <antoine.vandecreme@nist.gov>'s OpenSeadragon Scalebar plugin
 * https://github.com/usnistgov/OpenSeadragonScalebar
 */
/* eslint react/forbid-prop-types: off */
/* eslint no-lonely-if: off */
import React from 'react'
import PropTypes from 'prop-types'

// slide mpp: 0.2428 micrometers / 1 pixel

class Scalebar extends React.Component {
  constructor() {
    super()
    this.state = {
      backgroundColor: `lightblue`,
      green: `lightblue`,
      maxOptimalZoom: null,
      properties: {
        imageZoomPercent: 0,
        size: 0,
        text: '',
      },
      red: `#ffa083`,
    }
  }

  componentDidMount() {
    this.minWidth = 150
  }

  componentDidUpdate(prevProps) {
    const { viewer } = this.props

    if (prevProps.viewer !== viewer && viewer && viewer.viewport) {
      viewer.addHandler('open', () => {
        this.maxLevel = viewer.world.getItemAt(0).source.maxLevel
      })
      viewer.addHandler('animation', this.refresh)
      viewer.addHandler('resize', this.refresh)
      viewer.addHandler('update-level', (e) => {
        const { maxOptimalZoom } = this.state
        if (this.maxLevel && e.level === this.maxLevel) {
          if (!maxOptimalZoom) {
            const bestZoom = viewer.viewport.getZoom()
            this.setState({ maxOptimalZoom: bestZoom })
          }
        }
      })
      viewer.addHandler('zoom', (e) => {
        const { green, maxOptimalZoom, red } = this.state
        if (maxOptimalZoom) {
          let backgroundColor
          if (e.zoom > maxOptimalZoom) {
            backgroundColor = red
          } else {
            backgroundColor = green
          }
          this.setState({ backgroundColor })
        }
      })
      this.refresh()
    }
  }

  componentWillUnmount() {
    const { viewer } = this.props

    if (viewer && viewer.removeHandler) {
      viewer.removeHandler('animation', this.refresh)
      viewer.removeHandler('resize', this.refresh)
    }
  }

  getSignificand = (x) => {
    return x * 10 ** Math.ceil(-this.log10(x))
  }

  getSizeAndTextForMetric = (ppm, minSize, unitSuffix) => {
    const value = this.normalize(ppm, minSize)
    const factor = this.roundSignificand((value / ppm) * minSize, 3)
    const size = value * minSize
    const valueWithUnit = this.getWithUnit(factor, unitSuffix)
    return {
      size,
      text: valueWithUnit,
    }
  }

  getWithUnit = (value, unitSuffix) => {
    if (value < 0.000001) {
      return `${value * 1000000000} n${unitSuffix}`
    }
    if (value < 0.001) {
      return `${value * 1000000} μ${unitSuffix}`
    }
    if (value < 1) {
      return `${value * 1000} m${unitSuffix}`
    }
    if (value >= 1000) {
      return `${value / 1000} k${unitSuffix}`
    }
    return `${value} ${unitSuffix}`
  }

  log10 = (x) => {
    return Math.log(x) / Math.log(10)
  }

  normalize = (value, minSize) => {
    const significand = this.getSignificand(value)
    const minSizeSign = this.getSignificand(minSize)
    let result = this.getSignificand(significand / minSizeSign)
    if (result >= 5) {
      result /= 5
    }
    if (result >= 4) {
      result /= 4
    }
    if (result >= 2) {
      result /= 2
    }
    return result
  }

  refresh = () => {
    const { pixelsPerMeter, viewer } = this.props
    const zoom = viewer.viewport.viewportToImageZoom(viewer.viewport.getZoom(true))
    const currentPPM = zoom * pixelsPerMeter
    const imageZoomPercent = zoom * 100

    const properties = this.getSizeAndTextForMetric(currentPPM, this.minWidth, 'm')
    properties.imageZoomPercent = (Math.ceil(imageZoomPercent * 10) / 10).toFixed(1)
    this.setState({ properties })
  }

  roundSignificand = (x, decimalPlaces) => {
    const exponent = -Math.ceil(-this.log10(x))
    const power = decimalPlaces - exponent
    const significand = x * 10 ** power
    // To avoid rounding problems, always work with integers
    if (power < 0) {
      return Math.round(significand) * 10 ** -power
    }
    return Math.round(significand) / 10 ** power
  }

  render() {
    const {
      backgroundColor,
      properties: { imageZoomPercent, size, text },
    } = this.state
    const { classname } = this.props

    return (
      <>
        <div
          className={`imageZoom ${classname === 'leftViewer' ? 'imageZoomLeft' : 'imageZoomRight'}`}
          style={{
            backgroundColor,
          }}
        >
          {`${imageZoomPercent}x`}
        </div>
        <div
          className={`scalebar ${classname}`}
          style={{
            backgroundColor,
            width: size,
          }}
        >
          {text}
        </div>
      </>
    )
  }
}

Scalebar.defaultProps = {
  classname: 'rightViewer',
  pixelsPerMeter: 4118616.1449752883,
  viewer: {},
}

Scalebar.propTypes = {
  classname: PropTypes.string,
  pixelsPerMeter: PropTypes.number,
  viewer: PropTypes.object,
}

export default Scalebar
